import React, { useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ExportPage() {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState('clients');
  const [format, setFormat] = useState('xlsx');
  const [filters, setFilters] = useState({
    match_id: '',
    start_date: '',
    end_date: ''
  });

  const exportToXLSX = (data, columns, filename) => {
    // Transform data to use column labels
    const transformedData = data.map(row => {
      const newRow = {};
      columns.forEach(col => {
        newRow[col.label] = row[col.key];
      });
      return newRow;
    });

    const ws = XLSX.utils.json_to_sheet(transformedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const exportToPDF = (data, columns, filename, title) => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    
    // Date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    
    // Table
    const headers = columns.map(col => col.label);
    const rows = data.map(row => columns.map(col => {
      const value = row[col.key];
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') return value.toLocaleString();
      return value || '-';
    }));

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [212, 175, 55] },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    doc.save(`${filename}.pdf`);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      
      let url = `${API_URL}/api/export/${exportType}`;
      const params = new URLSearchParams();
      
      if (exportType === 'transactions') {
        if (filters.match_id) params.append('match_id', filters.match_id);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const result = await response.json();
      
      if (result.data.length === 0) {
        toast.warning('No data to export');
        return;
      }
      
      const titles = {
        clients: 'Clients Report',
        transactions: 'Transactions Report',
        'trial-balance': 'Trial Balance Report'
      };
      
      if (format === 'xlsx') {
        exportToXLSX(result.data, result.columns, result.filename);
        toast.success('XLSX file downloaded');
      } else {
        exportToPDF(result.data, result.columns, result.filename, titles[exportType]);
        toast.success('PDF file downloaded');
      }
    } catch (error) {
      console.error(error);
      toast.error('Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="export-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold">Export Data</h1>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <GroupBox label="Export Settings" className="p-4">
          <div className="space-y-4">
            <div>
              <Label>Report Type</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger data-testid="export-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clients">Clients</SelectItem>
                  <SelectItem value="transactions">Transactions</SelectItem>
                  <SelectItem value="trial-balance">Trial Balance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel (XLSX)
                    </div>
                  </SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      PDF
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {exportType === 'transactions' && (
              <>
                <div>
                  <Label>Start Date (Optional)</Label>
                  <Input 
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                    data-testid="export-start-date"
                  />
                </div>
                <div>
                  <Label>End Date (Optional)</Label>
                  <Input 
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                    data-testid="export-end-date"
                  />
                </div>
              </>
            )}

            <Button 
              className="w-full" 
              onClick={handleExport} 
              disabled={loading}
              data-testid="export-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export {format.toUpperCase()}
            </Button>
          </div>
        </GroupBox>

        <GroupBox label="Export Information" className="p-4">
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Available Reports</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li><strong>Clients</strong> - All active clients with their settings</li>
                <li><strong>Transactions</strong> - Match and session transactions</li>
                <li><strong>Trial Balance</strong> - Client balances and commissions</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Export Formats</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li><strong>XLSX</strong> - Microsoft Excel format, best for data analysis</li>
                <li><strong>PDF</strong> - Portable document format, best for printing/sharing</li>
              </ul>
            </div>
            
            <div className="p-3 bg-muted rounded-md">
              <p className="text-xs text-muted-foreground">
                💡 Tip: Use date filters for transaction exports to narrow down the data range.
              </p>
            </div>
          </div>
        </GroupBox>
      </div>
    </div>
  );
}
